import FirebaseAdapter from "@wc/adapters/firebase";
import { RolesNumericEnum } from "@wc/domain/authz/constants";

/**
 * Deletes a user account with proper cleanup of org/team memberships
 * - If user is only member of an org → delete org and all its teams
 * - If user is only admin of an org → promote oldest member to admin
 * - Same logic applies to teams
 * @param {string} userId The user to delete
 * @returns {Promise<void>}
 */
export async function deleteAccount(userId) {
  const userOrgs = await FirebaseAdapter().membership.listUserOrgs(userId, {
    limit: 0,
  });

  for (const orgMembership of userOrgs) {
    await handleOrgDeparture(userId, orgMembership.organizationId);
  }

  await FirebaseAdapter().users.deleteUser(userId, userId);
}

/**
 * Handles user leaving an organization
 * @param {string} userId The user leaving
 * @param {string} orgId The org to leave
 */
async function handleOrgDeparture(userId, orgId) {
  const members = await FirebaseAdapter().membership.listOrgMembers(orgId, {
    limit: 0,
  });

  if (members.length === 1) {
    await deleteOrgAndTeams(orgId, userId);
    return;
  }

  const userRole = await FirebaseAdapter().roles.getOrgRole(userId, orgId);
  const isAdmin = userRole && userRole.role === RolesNumericEnum.ORG_ADMIN;

  if (isAdmin) {
    const otherAdmins = await findOtherOrgAdmins(orgId, userId);
    if (otherAdmins.length === 0) {
      const otherMembers = members.filter((m) => m.userId !== userId);
      await promoteOldestToOrgAdmin(otherMembers, orgId, userId);
    }
  }

  const userTeamsInOrg = await FirebaseAdapter().membership.listUserTeamsInOrg(
    userId,
    orgId,
  );
  for (const teamId of userTeamsInOrg) {
    await handleTeamDeparture(userId, teamId, orgId);
  }

  await FirebaseAdapter().roles.removeOrgRole(orgId, userId);
  await FirebaseAdapter().membership.removeUserFromOrg(userId, orgId);
}

/**
 * Handles user leaving a team
 * @param {string} userId The user leaving
 * @param {string} teamId The team to leave
 */
async function handleTeamDeparture(userId, teamId) {
  const members = await FirebaseAdapter().membership.listTeamMembers(teamId, {
    limit: 0,
  });

  if (members.length === 1) {
    await FirebaseAdapter().teams.deleteTeam(teamId, userId);
    return;
  }

  const userRole = await FirebaseAdapter().roles.getTeamRole(userId, teamId);
  const isAdmin = userRole && userRole.role === RolesNumericEnum.TEAM_ADMIN;

  if (isAdmin) {
    const otherAdmins = await findOtherTeamAdmins(teamId, userId);
    if (otherAdmins.length === 0) {
      const otherMembers = members.filter((m) => m.userId !== userId);
      await promoteOldestToTeamAdmin(otherMembers, teamId, userId);
    }
  }

  await FirebaseAdapter().roles.removeTeamRole(teamId, userId);
  await FirebaseAdapter().membership.removeUserFromTeam(userId, teamId);
}

/**
 * Deletes an org and all its teams
 * @param {string} orgId The org to delete
 * @param {string} deletedBy The user performing the deletion
 */
async function deleteOrgAndTeams(orgId, deletedBy) {
  const teams = await FirebaseAdapter().teams.listTeamsByOrganization(orgId, {
    limit: 0,
  });

  for (const team of teams) {
    await FirebaseAdapter().teams.deleteTeam(team.id, deletedBy);
  }

  await FirebaseAdapter().organizations.deleteOrganization(orgId, deletedBy);
}

/**
 * Finds other org admins besides the specified user
 * @param {string} orgId The org to check
 * @param {string} excludeUserId The user to exclude
 * @returns {Promise<Array>} Other admin role assignments
 */
async function findOtherOrgAdmins(orgId, excludeUserId) {
  const admins = await FirebaseAdapter().roles.listOrgRolesByRole(
    orgId,
    RolesNumericEnum.ORG_ADMIN,
  );
  return admins.filter((a) => a.userId !== excludeUserId);
}

/**
 * Finds other team admins besides the specified user
 * @param {string} teamId The team to check
 * @param {string} excludeUserId The user to exclude
 * @returns {Promise<Array>} Other admin role assignments
 */
async function findOtherTeamAdmins(teamId, excludeUserId) {
  const admins = await FirebaseAdapter().roles.listTeamRolesByRole(
    teamId,
    RolesNumericEnum.TEAM_ADMIN,
  );
  return admins.filter((a) => a.userId !== excludeUserId);
}

/**
 * Promotes the oldest member to org admin
 * @param {Array} members List of members (excluding current user)
 * @param {string} orgId The org
 * @param {string} assignedBy Who is assigning
 */
async function promoteOldestToOrgAdmin(members, orgId, assignedBy) {
  const sorted = [...members].sort(
    (a, b) => a.joinedAt?.toMillis?.() - b.joinedAt?.toMillis?.(),
  );
  const oldest = sorted[0];
  if (oldest) {
    await FirebaseAdapter().roles.assignOrgRole(
      oldest.userId,
      orgId,
      RolesNumericEnum.ORG_ADMIN,
      assignedBy,
    );
  }
}

/**
 * Promotes the oldest member to team admin
 * @param {Array} members List of members (excluding current user)
 * @param {string} teamId The team
 * @param {string} assignedBy Who is assigning
 */
async function promoteOldestToTeamAdmin(members, teamId, assignedBy) {
  const sorted = [...members].sort(
    (a, b) => a.joinedAt?.toMillis?.() - b.joinedAt?.toMillis?.(),
  );
  const oldest = sorted[0];
  if (oldest) {
    await FirebaseAdapter().roles.assignTeamRole(
      oldest.userId,
      teamId,
      RolesNumericEnum.TEAM_ADMIN,
      assignedBy,
    );
  }
}
